// Tipos de notificação
export const NOTIFICATION_TYPES = {
  CASE_CREATED: 'case_created',
  CASE_UPDATED: 'case_updated',
  CASE_STATUS_CHANGED: 'case_status_changed',
  CASE_ARCHIVED: 'case_archived',
  CASE_CHAT_MESSAGE: 'case_chat_message',
  CASE_FILE_UPLOADED: 'case_file_uploaded',
  CASE_DEADLINE_WARNING: 'case_deadline_warning',
  TRANSACTION_CREATED: 'transaction_created',
  TRANSACTION_UPDATED: 'transaction_updated',
  TRANSACTION_PAID: 'transaction_paid',
  TRANSACTION_FAILED: 'transaction_failed',
  USER_CREATED: 'user_created',
  USER_UPDATED: 'user_updated',
  PRODUCT_CREATED: 'product_created',
  PRODUCT_UPDATED: 'product_updated',
  SYSTEM_ANNOUNCEMENT: 'system_announcement',
  SYSTEM_MAINTENANCE: 'system_maintenance'
}

export class Notification {
  constructor() {
    this.id = 0
    this.userId = 0
    this.type = ''
    this.title = ''
    this.message = ''
    this.data = {}
    this.read = false
    this.readAt = null
    this.createdAt = null
    this.updatedAt = null
    this.expiresAt = null
    this.priority = 'normal' // low, normal, high, urgent
    this.category = 'general' // general, case, transaction, user, product, system
    this.actionUrl = ''
    this.actionText = ''
    this.icon = ''
    this.color = ''
  }

  static FromObj(obj) {
    const instance = new Notification()
    
    instance.id = obj.id || 0
    instance.userId = obj.userId || 0
    instance.type = obj.type || ''
    instance.title = obj.title || ''
    instance.message = obj.message || ''
    instance.data = obj.data || {}
    instance.read = obj.read || false
    instance.readAt = obj.readAt || null
    instance.createdAt = obj.createdAt || null
    instance.updatedAt = obj.updatedAt || null
    instance.expiresAt = obj.expiresAt || null
    instance.priority = obj.priority || 'normal'
    instance.category = obj.category || 'general'
    instance.actionUrl = obj.actionUrl || ''
    instance.actionText = obj.actionText || ''
    instance.icon = obj.icon || ''
    instance.color = obj.color || ''
    
    return instance
  }

  // Status
  isRead() {
    return this.read
  }

  isUnread() {
    return !this.read
  }

  markAsRead() {
    this.read = true
    this.readAt = new Date()
  }

  markAsUnread() {
    this.read = false
    this.readAt = null
  }

  // Prioridade
  isLowPriority() {
    return this.priority === 'low'
  }

  isNormalPriority() {
    return this.priority === 'normal'
  }

  isHighPriority() {
    return this.priority === 'high'
  }

  isUrgentPriority() {
    return this.priority === 'urgent'
  }

  // Categoria
  isGeneralCategory() {
    return this.category === 'general'
  }

  isCaseCategory() {
    return this.category === 'case'
  }

  isTransactionCategory() {
    return this.category === 'transaction'
  }

  isUserCategory() {
    return this.category === 'user'
  }

  isProductCategory() {
    return this.category === 'product'
  }

  isSystemCategory() {
    return this.category === 'system'
  }

  // Formatação
  getPriorityLabel() {
    const labels = {
      low: 'Baixa',
      normal: 'Normal',
      high: 'Alta',
      urgent: 'Urgente'
    }
    
    return labels[this.priority] || 'Normal'
  }

  getPriorityColor() {
    const colors = {
      low: 'light-secondary',
      normal: 'primary',
      high: 'warning',
      urgent: 'danger'
    }
    
    return colors[this.priority] || 'primary'
  }

  getCategoryLabel() {
    const labels = {
      general: 'Geral',
      case: 'Caso',
      transaction: 'Transação',
      user: 'Usuário',
      product: 'Produto',
      system: 'Sistema'
    }
    
    return labels[this.category] || 'Geral'
  }

  getCategoryColor() {
    const colors = {
      general: 'secondary',
      case: 'primary',
      transaction: 'success',
      user: 'info',
      product: 'warning',
      system: 'danger'
    }
    
    return colors[this.category] || 'secondary'
  }

  getTypeIcon() {
    const icons = {
      [NOTIFICATION_TYPES.CASE_CREATED]: 'FileText',
      [NOTIFICATION_TYPES.CASE_UPDATED]: 'Edit',
      [NOTIFICATION_TYPES.CASE_STATUS_CHANGED]: 'RefreshCw',
      [NOTIFICATION_TYPES.CASE_ARCHIVED]: 'Archive',
      [NOTIFICATION_TYPES.CASE_CHAT_MESSAGE]: 'MessageCircle',
      [NOTIFICATION_TYPES.CASE_FILE_UPLOADED]: 'Upload',
      [NOTIFICATION_TYPES.CASE_DEADLINE_WARNING]: 'AlertTriangle',
      [NOTIFICATION_TYPES.TRANSACTION_CREATED]: 'CreditCard',
      [NOTIFICATION_TYPES.TRANSACTION_UPDATED]: 'DollarSign',
      [NOTIFICATION_TYPES.TRANSACTION_PAID]: 'CheckCircle',
      [NOTIFICATION_TYPES.TRANSACTION_FAILED]: 'XCircle',
      [NOTIFICATION_TYPES.USER_CREATED]: 'UserPlus',
      [NOTIFICATION_TYPES.USER_UPDATED]: 'User',
      [NOTIFICATION_TYPES.PRODUCT_CREATED]: 'Package',
      [NOTIFICATION_TYPES.PRODUCT_UPDATED]: 'Package',
      [NOTIFICATION_TYPES.SYSTEM_ANNOUNCEMENT]: 'Speaker',
      [NOTIFICATION_TYPES.SYSTEM_MAINTENANCE]: 'Tool'
    }
    
    return this.icon || icons[this.type] || 'Bell'
  }

  getDisplayColor() {
    if (this.color) return this.color
    
    return this.getCategoryColor()
  }

  // Datas
  getFormattedCreatedAt() {
    if (!this.createdAt) return ''
    
    const date = new Date(this.createdAt)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffMinutes = Math.ceil(diffTime / (1000 * 60))
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffMinutes < 60) {
      return `${diffMinutes} min atrás`
    } else if (diffHours < 24) {
      return `${diffHours}h atrás`
    } else if (diffDays === 1) {
      return 'Ontem'
    } else if (diffDays < 7) {
      return `${diffDays} dias atrás`
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
  }

  getFormattedReadAt() {
    if (!this.readAt) return ''
    
    const date = new Date(this.readAt)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Validações
  isValid() {
    return this.userId > 0 && this.type && this.title && this.message
  }

  isExpired() {
    if (!this.expiresAt) return false
    
    return new Date() > new Date(this.expiresAt)
  }

  getDaysUntilExpiry() {
    if (!this.expiresAt) return null
    
    const now = new Date()
    const expiry = new Date(this.expiresAt)
    const timeDiff = expiry.getTime() - now.getTime()
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))
    
    return daysDiff
  }

  // Ações
  hasAction() {
    return !!this.actionUrl
  }

  getActionLabel() {
    return this.actionText || 'Ver'
  }

  // Métodos utilitários
  getData(key) {
    return this.data[key] || null
  }

  setData(key, value) {
    this.data[key] = value
  }

  hasData(key) {
    return key in this.data
  }

  // Dados para formulário
  toFormData() {
    return {
      id: this.id,
      userId: this.userId,
      type: this.type,
      title: this.title,
      message: this.message,
      data: this.data,
      priority: this.priority,
      category: this.category,
      actionUrl: this.actionUrl,
      actionText: this.actionText,
      icon: this.icon,
      color: this.color,
      expiresAt: this.expiresAt
    }
  }
}